import { useCallback, useMemo } from "react";
import { useBusyBlockerContext } from "../contexts/busy-blocker-context";
import { useDefinition } from "./useDefinition";
import { useTextZoneDashboard } from "./useTextZoneDashboard";

export function useUserEventsValidation() {
	const { busyBlocker } = useBusyBlockerContext();
	const dashboard = useTextZoneDashboard();
	const definition = useDefinition();

	const validateDataSourcesFitAsync = useCallback(
		(dataSources: eXpress.creator.calendars.ICalendarDataSources) => {
			const textOutOfBounds = {
				details: { affectedItems: [{}] },
			} as eXpress.core.ITextOutsideBounds;

			if (!definition) return Promise.resolve(textOutOfBounds);

			// Block while fetching assets (like font metrics)
			const res = busyBlocker.during(
				// Get Zones that don't fit
				eXpress.core.validateTextOutsideBoundsAsync(
					definition,
					dashboard.getFontSizeCorrectorAsync,
					(zone) => {
						if (!eXpress.core.hasDataSource(zone)) return ""; // Don't measure this zone

						// Get the New Text from the dataSources
						const dataSource =
							zone.dataSource as keyof eXpress.creator.calendars.ICalendarDataSources;
						const dateCode = zone.dateCode
							? eXpress.RecurringDateCode.fromDateCode(zone.dateCode)
							: undefined;

						switch (dataSource) {
							case "UserDate": {
								// Get new Value to apply
								const val = dataSources[dataSource];
								if (!val) return ""; // Don't measure this zone

								const valuesToApply = val
									.filter((item) => item.dateCode === dateCode)
									.map((item) => item.value);

								return (
									eXpress.creator.calendars.eventsToTextValidation(
										valuesToApply,
									) || ""
								);
							}

							case "Holiday":
							case "FlagDay":
							case "NameDay": {
								const val = dataSources[dataSource];
								// Measure current text
								return val && val.show ? zone.text : ""; // Don't measure this zone
							}

							default: {
								eXpress.utils.exhaustiveFail(dataSource, true);
								return ""; // Don't measure this zone
							}
						}
					},
				),
			);

			return res.then((result) => {
				if (result) {
					return result;
				}
				return textOutOfBounds;
			});
		},
		[busyBlocker, dashboard.getFontSizeCorrectorAsync, definition],
	);

	const validateUnusedUserDates = useCallback(
		(
			newUserDates: eXpress.creator.calendars.ICalendarDataSources["UserDate"],
		): eXpress.creator.calendars.IUnusedUserDateValidation => {
			const def = eXpress.utils.required(definition, "definition");
			const definitionDataSources =
				eXpress.creator.calendars.calendarUtils.getCalendarDataSources(def);
			const userDateDataSource = eXpress.DataSource.fromString("UserDate");

			const unusedUserDates = eXpress.utils.isArray(newUserDates)
				? newUserDates
						.filter((dataSource) => {
							if (!dataSource.dateCode) return false;
							return !definitionDataSources.UserDate?.some(
								(i) => i.dateCode === dataSource.dateCode,
							);
						})
						.map((dataSourceItem) => {
							const result = {
								dataSource: userDateDataSource,
								dateCode: dataSourceItem.dateCode,
							};
							return result;
						})
				: undefined;

			if (eXpress.utils.isNonEmptyArray(unusedUserDates)) {
				const validationMessage: eXpress.creator.calendars.IUnusedUserDateValidation = {
					name: "UnusedUserDate",
					isError: true,
					details: {
						affectedDataSources: unusedUserDates,
					},
				};
				return validationMessage;
			}

			return {
				name: "UnusedUserDate",
				isError: true,
				details: {
					affectedDataSources: [],
				},
			} as eXpress.creator.calendars.IUnusedUserDateValidation;
		},
		[definition],
	);

	const getMaxNumberOfEvents = useCallback(
		(dateDataSource: eXpress.core.IDateDataSource) => {
			if (!definition) return 0;
			const allMaxNumberOfEvents = definition
				.textZones()
				// Only get textZone of UserDate and date
				.filter(
					(zone) =>
						eXpress.core.hasDataSource(zone) &&
						zone.dataSource === "UserDate" &&
						zone.dateCode &&
						eXpress.RecurringDateCode.fromDateCode(zone.dateCode) ===
							dateDataSource.dateCode,
				)
				// Get maxNumberOfEvents of each zone
				.map((zone) => zone.maxNumberOfEvents ?? 1); // Use default 1 event

			return allMaxNumberOfEvents.length === 0 ? 0 : Math.min(...allMaxNumberOfEvents);
		},
		[definition],
	);

	const validator: Pick<
		eXpress.creator.calendars.ICalendarEvents,
		"getMaxNumberOfEvents" | "validateDataSourcesFitAsync" | "validateUnusedUserDates"
	> = useMemo(
		() => ({
			validateDataSourcesFitAsync,
			validateUnusedUserDates,
			getMaxNumberOfEvents,
		}),
		[getMaxNumberOfEvents, validateDataSourcesFitAsync, validateUnusedUserDates],
	);

	const validateUserDatesAsync = useCallback(
		(
			userEventData: eXpress.collections.Dictionary<
				eXpress.RecurringDateCode,
				eXpress.core.IUserDate[]
			>,
		) => {
			return eXpress.creator.calendars.calendarUtils.validateInputAsync(
				validator,
				userEventData,
				definition?.product.amountOfPersonalEvents ?? 0,
			);
		},
		[definition, validator],
	);

	return validateUserDatesAsync;
}
